import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './App.css';

function App() {
  const [text, setText] = useState('');
  const [voiceId, setVoiceId] = useState('');
  const [voices, setVoices] = useState([]);
  const [outputFormat, setOutputFormat] = useState('mp3_44100_128');
  const [loading, setLoading] = useState(false);
  const [story, setStory] = useState(null);
  const [error, setError] = useState(null);
  
  // Custom voice parameters
  const [stability, setStability] = useState(0.5);
  const [similarityBoost, setSimilarityBoost] = useState(0.75);
  const [style, setStyle] = useState(0.0);
  const [speed, setSpeed] = useState(1.0);
  const [useSpeakerBoost, setUseSpeakerBoost] = useState(true);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Load available voices on startup
  useEffect(() => {
    fetchVoices();
  }, []);

  const fetchVoices = async () => {
    try {
      const response = await axios.get('/earstory-poc-api/api/story/voices');
      setVoices(response.data.voices || []);
      if (response.data.voices && response.data.voices.length > 0) {
        setVoiceId(response.data.voices[0].voice_id);
      }
    } catch (err) {
      console.error('Error fetching voices:', err);
      setError('Unable to load available voices');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!text.trim()) {
      setError('Please enter some text!');
      return;
    }

    setLoading(true);
    setError(null);
    setStory(null);

    try {
      const response = await axios.post('/earstory-poc-api/api/story/text', {
        text: text,
        voiceId: voiceId || null,
        outputFormat: outputFormat,
        voiceSettings: {
          stability: stability,
          similarityBoost: similarityBoost,
          style: style,
          speed: speed,
          useSpeakerBoost: useSpeakerBoost
        }
      });

      setStory(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error creating story:', err);
      setError('Error creating story: ' + (err.response?.data?.error || err.message));
      setLoading(false);
    }
  };

  const resetSettings = () => {
    setStability(0.5);
    setSimilarityBoost(0.75);
    setStyle(0.0);
    setSpeed(1.0);
    setUseSpeakerBoost(true);
  };

  const getVoiceLabel = (voice) => {
    const accent = voice.labels?.accent || '';
    const gender = voice.labels?.gender || '';
    const age = voice.labels?.age || '';
    return `${voice.name} (${gender}, ${age}, ${accent})`;
  };

  return (
    <div className="App">
      <header className="App-header">
        <h1>Earstory</h1>
      </header>

      <main className="App-main">
        <form onSubmit={handleSubmit} className="story-form">
          
          {/* Text area */}
          <div className="form-group">
            <label htmlFor="text">Write your story:</label>
            <textarea
              id="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Enter the text you want to transform into audio..."
              rows="8"
              disabled={loading}
            />
          </div>

          {/* Voice selection dropdown */}
          <div className="form-group">
            <label htmlFor="voice">Select a voice:</label>
            <select
              id="voice"
              value={voiceId}
              onChange={(e) => setVoiceId(e.target.value)}
              disabled={loading || voices.length === 0}
            >
              {voices.length === 0 ? (
                <option>Loading voices...</option>
              ) : (
                voices.map((voice) => (
                  <option key={voice.voice_id} value={voice.voice_id}>
                    {getVoiceLabel(voice)}
                  </option>
                ))
              )}
            </select>
          </div>

          {/* Audio Quality selection */}
          <div className="form-group">
            <label htmlFor="audioQuality">Audio Quality:</label>
            <select
              id="audioQuality"
              value={outputFormat}
              onChange={(e) => setOutputFormat(e.target.value)}
              disabled={loading}
            >
              <optgroup label="MP3 - Compressed (Recommended)">
                <option value="mp3_22050_32">MP3 22kHz 32kbps - Smallest</option>
                <option value="mp3_24000_48">MP3 24kHz 48kbps</option>
                <option value="mp3_44100_64">MP3 44kHz 64kbps - Small</option>
                <option value="mp3_44100_96">MP3 44kHz 96kbps - Good</option>
                <option value="mp3_44100_128">MP3 44kHz 128kbps - High (Default)</option>
                <option value="mp3_44100_192">MP3 44kHz 192kbps - Very High</option>
              </optgroup>
              
              <optgroup label="Opus - Modern Codec">
                <option value="opus_48000_32">Opus 48kHz 32kbps</option>
                <option value="opus_48000_64">Opus 48kHz 64kbps</option>
                <option value="opus_48000_96">Opus 48kHz 96kbps</option>
                <option value="opus_48000_128">Opus 48kHz 128kbps</option>
                <option value="opus_48000_192">Opus 48kHz 192kbps</option>
              </optgroup>
              
              <optgroup label="PCM - Uncompressed">
                <option value="pcm_16000">PCM 16kHz</option>
                <option value="pcm_22050">PCM 22kHz</option>
                <option value="pcm_24000">PCM 24kHz</option>
                <option value="pcm_44100">PCM 44kHz - CD Quality</option>
                <option value="pcm_48000">PCM 48kHz - Professional</option>
              </optgroup>
              
              <optgroup label="Telephony">
                <option value="ulaw_8000">μ-Law 8kHz</option>
                <option value="alaw_8000">A-Law 8kHz</option>
              </optgroup>
            </select>
            <p className="param-description">
              Higher quality = larger file size. MP3 High (128kbps) is recommended for most use cases.
            </p>
          </div>

          {/* Advanced Parameters Section */}
          <div className="advanced-section">
            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="toggle-advanced-btn"
              disabled={loading}
            >
              {showAdvanced ? 'Hide' : 'Show'} Advanced Parameters
            </button>

            {showAdvanced && (
              <div className="advanced-params">
                
                {/* Stability Slider */}
                <div className="param-group">
                  <label htmlFor="stability">
                    Stability: <strong>{stability.toFixed(2)}</strong>
                    <span className="param-info">
                      {stability < 0.3 ? ' Very emotional' : 
                       stability < 0.7 ? ' Balanced' : 
                       ' Monotone'}
                    </span>
                  </label>
                  <input
                    type="range"
                    id="stability"
                    min="0"
                    max="1"
                    step="0.05"
                    value={stability}
                    onChange={(e) => setStability(parseFloat(e.target.value))}
                    disabled={loading}
                  />
                  <p className="param-description">
                    Low = more emotional and variable | High = more stable and monotone
                  </p>
                </div>

                {/* Similarity Boost Slider */}
                <div className="param-group">
                  <label htmlFor="similarity">
                    Similarity: <strong>{similarityBoost.toFixed(2)}</strong>
                  </label>
                  <input
                    type="range"
                    id="similarity"
                    min="0"
                    max="1"
                    step="0.05"
                    value={similarityBoost}
                    onChange={(e) => setSimilarityBoost(parseFloat(e.target.value))}
                    disabled={loading}
                  />
                  <p className="param-description">
                    How closely the voice should match the original (recommended: 0.75)
                  </p>
                </div>

                {/* Style Slider */}
                <div className="param-group">
                  <label htmlFor="style">
                    Style: <strong>{style.toFixed(2)}</strong>
                    {style > 0 && <span className="param-warning"> Increases latency</span>}
                  </label>
                  <input
                    type="range"
                    id="style"
                    min="0"
                    max="1"
                    step="0.05"
                    value={style}
                    onChange={(e) => setStyle(parseFloat(e.target.value))}
                    disabled={loading}
                  />
                  <p className="param-description">
                    Style exaggeration (recommended: 0.0 for stability)
                  </p>
                </div>

                {/* Speed Slider */}
                <div className="param-group">
                  <label htmlFor="speed">
                    Speed: <strong>{speed.toFixed(2)}x</strong>
                    <span className="param-info">
                      {speed < 0.9 ? ' Slow' : 
                       speed < 1.1 ? ' Normal' : 
                       ' Fast'}
                    </span>
                  </label>
                  <input
                    type="range"
                    id="speed"
                    min="0.7"
                    max="1.2"
                    step="0.05"
                    value={speed}
                    onChange={(e) => setSpeed(parseFloat(e.target.value))}
                    disabled={loading}
                  />
                  <p className="param-description">
                    Speaking speed: 0.7x (slow) to 1.2x (fast). Default: 1.0x
                  </p>
                </div>

                {/* Speaker Boost Checkbox */}
                <div className="param-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={useSpeakerBoost}
                      onChange={(e) => setUseSpeakerBoost(e.target.checked)}
                      disabled={loading}
                    />
                    <span>Speaker Boost (improves similarity, increases latency)</span>
                  </label>
                </div>

                {/* Reset Button */}
                <button
                  type="button"
                  onClick={resetSettings}
                  className="reset-btn"
                  disabled={loading}
                >
                  Reset Parameters
                </button>
              </div>
            )}
          </div>

          {/* Submit button */}
          <button type="submit" disabled={loading} className="submit-btn">
            {loading ? 'Generating...' : 'Generate Audio'}
          </button>
        </form>

        {/* Show errors */}
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {/* Show result */}
        {story && story.status === 'COMPLETED' && (
          <div className="result">
            <h2>Story created successfully!</h2>
            <div className="story-info">
              <p><strong>ID:</strong> {story.id}</p>
              <p><strong>Type:</strong> {story.type}</p>
              <p><strong>Created:</strong> {new Date(story.createdAt).toLocaleString('en-US')}</p>
            </div>
            
            <div className="audio-player">
              <h3>Listen to your story:</h3>
              <audio controls src={`/earstory-poc-api/api/story/audio/${story.audioUrl}`} preload="auto">
                Your browser does not support the audio element.
              </audio>
              <br />
              <a 
                href={`/earstory-poc-api/api/story/audio/${story.audioUrl}`} 
                download={story.audioUrl}
                className="download-btn"
              >
                Download Audio
              </a>
            </div>

            <div className="story-content">
              <h3>Original text:</h3>
              <p>{story.content}</p>
            </div>
          </div>
        )}

        {/* Show if story failed */}
        {story && story.status === 'FAILED' && (
          <div className="error-message">
            Generation failed: {story.errorMessage}
          </div>
        )}
      </main>

      <footer className="App-footer">
        <p>Powered by ElevenLabs API</p>
      </footer>
    </div>
  );
}

export default App;